import React, { Component } from "react";
import PropTypes from "prop-types";

import { styled } from "@mui/material/styles";
import { palette } from "styled-theme";

import IconButton from "@mui/material/IconButton";
import ViewListIcon from "@mui/icons-material/ViewList";
import CardTravelIcon from "@mui/icons-material/CardTravel";
import NavigationAppIcon from "@mui/icons-material/Apps";
import FormatAlignRightIcon from "@mui/icons-material/FormatAlignRight";
import BookmarkBorderIcon from "@mui/icons-material/BookmarkBorder";
import FormGroup from "@mui/material/FormGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import { grey } from "@mui/material/colors";
import AppBar from "@mui/material/AppBar";
import NewToolbar from "@mui/material/Toolbar";
import Tooltip from "@mui/material/Tooltip";

import SidebarToggleIcon from "~/components/Icon/SidebarToggleIcon";
import ModeSwitcher from "~/components/ModeSwitcher";
import PageControls from "~/components/PageControls";
import BookTitle from "~/components/BookTitle";
import Pagination from "~/components/Pagination";
import { hasUserAdminGroup } from "~/utils";

const white = grey[50];

const StyledSwitch = styled(Switch)({
  "& .MuiSwitch-switchBase": {
    color: "rgb(245, 245, 245)",
    "&.Mui-checked": {
      color: "rgb(245, 245, 245)"
    },
    "&.Mui-checked + .MuiSwitch-track": {
      backgroundColor: "#ff5800",
      opacity: 1
    }
  },

  "& .MuiSwitch-track": {
    backgroundColor: "rgba(172, 172, 172)",
    opacity: 1
  }
});

const tabIcons = {
  planner: CardTravelIcon,
  pages: NavigationAppIcon,
  outline: FormatAlignRightIcon,
  bookmarks: BookmarkBorderIcon
};

const StyledToolbar = styled(AppBar)(props => ({
  "&.MuiAppBar-root": {
    height: "48px !important",
    flexDirection: "row !important",
    justifyContent: "space-between",
    backgroundColor: `${props.theme.palette.grayscale[1]} !important`,
    textAlign: "center",
    position: "relative",
    paddingLeft: `${props.hasPaddingLeft ? "300px" : 0}`,
    paddingRight: `${props.hasPaddingRight ? "300px" : 0}`
  }
}));

const LeftIcon = styled(IconButton)(({ theme }) => ({
  "&.MuiIconButton-root": {
    backgroundColor: theme.palette.mbred[0],
    width: 48,
    height: 48,
    borderRadius: 0,
    "&:hover": {
      backgroundColor: theme.palette.mbred[0]
    }
  },
  "&:focus-visible": {
    outline: theme.outline(),
    outlineOffset: "-2px"
  }
}));

const RightIcon = styled(IconButton)(props => {
  let backgroundColor = props.theme.palette.mbgreen[0];

  if (props.isDlsActive) {
    backgroundColor = props.theme.palette.mborange[0];
  } else if (props.isPresentationActive) {
    backgroundColor = props.theme.palette.prblue[1];
  }

  return {
    "&.MuiIconButton-root": {
      backgroundColor,
      width: 48,
      height: 48,
      borderRadius: 0,
      "&:hover": {
        backgroundColor
      }
    },
    "&:focus-visible": {
      outline: props.theme.outline(),
      outlineOffset: "-2px"
    }
  };
});

const CenteredBox = styled("div")`
  position: absolute;
  left: calc(50% + ${props => (props.hasPaddingLeft ? 150 : 0)}px - ${props => (props.hasPaddingRight ? 150 : 0)}px);
  text-align: center;
  margin: 0 auto;
`;

const Wrapper = styled("div")`
  overflow-y: hidden;
  @media (max-width: 768px) {
    overflow-x: ${props => (props.isFloated ? "auto" : "visible")};
  }
`;

const LeftToolbarGroup = styled(NewToolbar)``;

const RightToolbarGroup = styled(NewToolbar)``;

const TitleWrapper = styled("div")(props => ({
  padding: "0 1em",
  height: "100%",
  "margin-right": "auto",

  "@media (max-width: 768px)": {
    padding: props.isFloated ? "0" : "0 1em"
  }
}));

const ToggleWrapper = styled("div")`
  margin: 0 1em;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 48px;
`;

const LogoWrapper = styled("div")`
  height: 48px;
  padding: 6px 0;
  margin: 0 12px;
  overflow: hidden;
  @media (max-width: 1024px) {
    width: 55px;
  }
`;

const StyledLogo = styled("img")`
  height: 100%;
  width: auto;
  user-select: none;
`;

class Header extends Component {
  static whyDidYouRender = false;

  onEditModeChange = event => this.props.setEditMode(event.target.checked);

  render() {
    const {
      isGreaterThanMedium,
      isLessThanSmall,
      isMaterialEnabled,
      isSidebarLeftOpen,
      isSidebarRightOpen,
      isHeaderVisible,
      onNextPageClick,
      onPreviousPageClick,
      isNotFirstPage,
      isNotLastPage,
      isSidebarLeftVisible,
      isSidebarRightVisible,
      isEditMode,
      isDlsActive,
      isPresentationActive,
      isDlsEnabled,
      toggleSidebarLeft,
      toggleSidebarRight,
      relativePage,
      goToDestination,
      showLeftSidebarSwitch,
      showRightSidebarSwitch,
      integrateMenu,
      firstVisibleTab
    } = this.props;

    let SidebarLeftToggleIcon = SidebarToggleIcon;

    if (firstVisibleTab) {
      SidebarLeftToggleIcon = tabIcons[firstVisibleTab];
    }

    const isAdmin = hasUserAdminGroup();

    return (
      isHeaderVisible && (
        <StyledToolbar
          className="header"
          position="static"
          hasPaddingLeft={isGreaterThanMedium && isSidebarLeftOpen && isSidebarLeftVisible}
          hasPaddingRight={isGreaterThanMedium && isSidebarRightOpen && isSidebarRightVisible}
        >
          <LeftToolbarGroup variant="dense" disableGutters>
            {showLeftSidebarSwitch && isSidebarLeftVisible && (
              <Tooltip title={`Navigationsleiste ${isSidebarLeftOpen ? "schließen" : "öffnen"}`}>
                <LeftIcon onClick={toggleSidebarLeft} size="large">
                  <SidebarLeftToggleIcon htmlColor={white} />
                </LeftIcon>
              </Tooltip>
            )}

            <LogoWrapper>
              <a href="http://www.mildenberger-digital.de/" target="_blank" rel="noopener noreferrer">
                <StyledLogo draggable={false} src="/static/images/logo.svg" alt="Logo" />
              </a>
            </LogoWrapper>
          </LeftToolbarGroup>

          {integrateMenu && (
            <TitleWrapper isFloated={integrateMenu}>
              <BookTitle />
            </TitleWrapper>
          )}

          <Wrapper isFloated={integrateMenu}>
            {!isDlsActive && (
              <CenteredBox
                hasPaddingLeft={isGreaterThanMedium && isSidebarLeftOpen && isSidebarLeftVisible}
                hasPaddingRight={isGreaterThanMedium && isSidebarRightOpen && isSidebarRightVisible}
              >
                <Pagination
                  page={relativePage}
                  goToPrevious={onPreviousPageClick}
                  goToNext={onNextPageClick}
                  goToDestination={goToDestination}
                  showGoToPrevious={isNotFirstPage}
                  showGoToNext={isNotLastPage}
                  isTransparent
                />
              </CenteredBox>
            )}
          </Wrapper>

          <RightToolbarGroup variant="dense" disableGutters>
            {integrateMenu && <PageControls />}

            {isAdmin && !integrateMenu && (
              <ToggleWrapper>
                <FormGroup row>
                  <FormControlLabel
                    control={<StyledSwitch checked={isEditMode} onChange={this.onEditModeChange} color="secondary" />}
                    label={isLessThanSmall ? "" : "Bearbeitungsmodus"}
                  />
                </FormGroup>
              </ToggleWrapper>
            )}

            {isDlsEnabled && <ModeSwitcher />}

            {isMaterialEnabled && showRightSidebarSwitch && isSidebarRightVisible && (
              <Tooltip title={`Materialleiste ${isSidebarRightOpen ? "schließen" : "öffnen"}`}>
                <div>
                  <RightIcon
                    isDlsActive={isDlsActive}
                    isPresentationActive={isPresentationActive}
                    onClick={toggleSidebarRight}
                    size="large"
                  >
                    <ViewListIcon htmlColor={white} />
                  </RightIcon>
                </div>
              </Tooltip>
            )}
          </RightToolbarGroup>
        </StyledToolbar>
      )
    );
  }
}

Header.propTypes = {
  isMaterialEnabled: PropTypes.bool.isRequired,
  isGreaterThanMedium: PropTypes.bool.isRequired,
  isSidebarLeftOpen: PropTypes.bool.isRequired,
  isSidebarRightOpen: PropTypes.bool.isRequired,
  isHeaderVisible: PropTypes.bool.isRequired,
  toggleSidebarLeft: PropTypes.func.isRequired,
  toggleSidebarRight: PropTypes.func.isRequired,
  onNextPageClick: PropTypes.func.isRequired,
  onPreviousPageClick: PropTypes.func.isRequired,
  isNotFirstPage: PropTypes.bool.isRequired,
  isNotLastPage: PropTypes.bool.isRequired,
  relativePage: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  setEditMode: PropTypes.func.isRequired,
  isLessThanSmall: PropTypes.bool.isRequired,
  isSidebarLeftVisible: PropTypes.bool.isRequired,
  isSidebarRightVisible: PropTypes.bool.isRequired,
  isEditMode: PropTypes.bool.isRequired,
  isDlsActive: PropTypes.bool.isRequired,
  isPresentationActive: PropTypes.bool.isRequired,
  isDlsEnabled: PropTypes.bool.isRequired,
  showLeftSidebarSwitch: PropTypes.bool.isRequired,
  showRightSidebarSwitch: PropTypes.bool.isRequired,
  integrateMenu: PropTypes.bool.isRequired,
  goToDestination: PropTypes.func.isRequired,
  firstVisibleTab: PropTypes.string
};

export default Header;
